import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { ErrorBox } from "@/components/common";
import type { ImportReceipt } from "@/components/DictionaryImporter";

type Field = "term" | "definition" | "data_entity" | "synonyms" | "lob" |
  "product" | "business_unit" | "jurisdiction" | "language";
const FIELDS: Field[] = ["term", "definition", "data_entity", "synonyms", "lob",
  "product", "business_unit", "jurisdiction", "language"];
type Buckets = { buckets: string[]; configured_bucket: string; limited_to_configured_bucket: boolean };
type Listing = { objects: { selection: string; label: string; size: number; modified_at: string | null }[]; truncated: boolean; max_file_bytes: number };
type Preview = { row_count: number; entity_count: number; unclassified_count: number;
  alias_count: number; rejected_count: number; rejected_rows: { row: number; reason: string }[];
  headers: string[]; header_row: number; column_map: Partial<Record<Field, number>>; warning?: string | null };
type Staged = Preview & { run_id: string; review_run_id: string; created_count: number; proposal_ids: string[]; proposal_count: number };

export function CloudDictionaryBrowser({ workspaceId, onStaged }: { workspaceId: string; onStaged?: (receipt: ImportReceipt) => void }) {
  const [bucket, setBucket] = useState("");
  const [prefix, setPrefix] = useState("");
  const [filter, setFilter] = useState("");
  const [loadedFilter, setLoadedFilter] = useState("");
  const [loadedPrefix, setLoadedPrefix] = useState<string | null>(null);
  const [selection, setSelection] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<Field, number>>>({});
  const [headerRow, setHeaderRow] = useState<number | undefined>();
  const [currentPreview, setCurrentPreview] = useState("");
  const [staged, setStaged] = useState<Staged | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const buckets = useQuery<Buckets>({
    queryKey: ["cloud-dictionary-buckets", workspaceId],
    queryFn: () => rawFetch(`/catalog/cloud/buckets/?workspace=${encodeURIComponent(workspaceId)}`),
    enabled: Boolean(workspaceId),
  });
  const selectedBucket = bucket || buckets.data?.configured_bucket || "";
  const objects = useQuery<Listing>({
    queryKey: ["cloud-dictionary-objects", workspaceId, selectedBucket, loadedPrefix, loadedFilter],
    queryFn: () => rawFetch(`/catalog/cloud/objects/?workspace=${encodeURIComponent(workspaceId)}&bucket=${encodeURIComponent(selectedBucket)}&prefix=${encodeURIComponent(loadedPrefix ?? "")}&filter=${encodeURIComponent(loadedFilter)}`),
    enabled: Boolean(workspaceId && selectedBucket && loadedPrefix !== null),
  });

  async function inspect(nextMapping?: Partial<Record<Field, number>>, nextHeader?: number) {
    if (!selection || !selectedBucket) return;
    setBusy(true); setError(null); setStaged(null);
    try {
      const result = await rawFetch<Preview>("/catalog/cloud/import/", {
        method: "POST", body: JSON.stringify({ workspace: workspaceId, bucket: selectedBucket,
          selection, preview: true, ...(nextMapping ? { column_map: nextMapping } : {}),
          ...(nextHeader ? { header_row: nextHeader } : {}),
        }),
      });
      setPreview(result); setMapping(result.column_map); setHeaderRow(result.header_row);
      setCurrentPreview(JSON.stringify(result.column_map));
    } catch (caught) { setError(caught as Error); }
    finally { setBusy(false); }
  }

  async function stage() {
    if (!selection || !preview || !selectedBucket) return;
    setBusy(true); setError(null);
    try {
      const result = await rawFetch<Staged>("/catalog/cloud/import/", {
        method: "POST", body: JSON.stringify({ workspace: workspaceId,
          bucket: selectedBucket, selection, column_map: mapping, header_row: headerRow }),
      });
      setStaged(result);
      onStaged?.({ runId: result.review_run_id || result.run_id,
        proposalCount: result.proposal_count, proposalIds: result.proposal_ids });
    } catch (caught) { setError(caught as Error); }
    finally { setBusy(false); }
  }

  return <section className="card space-y-3" aria-label="Browse cloud dictionary">
    <div><h2 className="font-semibold">Import from your S3 bucket</h2>
      <p className="text-xs text-muted">This connection lists buckets and reads only the file you select. It does not sync the bucket, write to it, or delete its files. CSV/XLSX bytes are processed in memory; reviewed definitions and metadata remain in the managed tenant database. Object keys are hidden.</p>
    </div>
    {buckets.isError && <ErrorBox error={buckets.error} />}
    {buckets.data && <div className="grid gap-2 sm:grid-cols-[1fr_2fr_2fr_auto]">
      <label className="text-xs text-muted">Bucket<select className="input mt-1 w-full" value={selectedBucket}
        onChange={(event) => { setBucket(event.target.value); setLoadedPrefix(null); setSelection(""); setPreview(null); }}>
        {buckets.data.buckets.map((name) => <option key={name} value={name}>{name}</option>)}
      </select></label>
      <label className="text-xs text-muted">Prefix<input className="input mt-1 w-full" value={prefix}
        maxLength={256} placeholder="dictionaries/" onChange={(event) => setPrefix(event.target.value)} /></label>
      <label className="text-xs text-muted">Filter files<input className="input mt-1 w-full" value={filter}
        maxLength={80} placeholder="e.g. motor" onChange={(event) => setFilter(event.target.value)} /></label>
      <button className="btn self-end" disabled={objects.isFetching}
        onClick={() => { setLoadedPrefix(prefix); setLoadedFilter(filter); setSelection(""); setPreview(null); if (loadedPrefix === prefix && loadedFilter === filter) void objects.refetch(); }}>List files</button>
    </div>}
    {buckets.data?.limited_to_configured_bucket && <p className="text-xs text-muted">This key cannot list all buckets, so only its configured bucket is shown.</p>}
    {objects.isError && <ErrorBox error={objects.error} />}
    {objects.data && <div className="max-h-48 overflow-y-auto border border-border p-2 text-xs">
      {objects.data.objects.length === 0 && <p className="text-muted">No CSV/XLSX objects at this prefix.</p>}
      {objects.data.objects.map((item) => <label key={item.selection} className="flex items-center gap-2 border-b border-border py-1">
        <input type="radio" name="cloud-dictionary-object" checked={selection === item.selection}
          onChange={() => { setSelection(item.selection); setPreview(null); setStaged(null); }} />
        <span className="min-w-0 flex-1 font-mono">{item.label}</span>
        <span className="shrink-0 text-muted">{item.size.toLocaleString()} bytes</span>
        <span className="shrink-0 text-muted">{item.modified_at ? new Date(item.modified_at).toLocaleDateString() : "Date unavailable"}</span>
      </label>)}
      {objects.data.truncated && <p className="mt-2 text-muted">First 500 objects shown. Narrow the prefix to see more.</p>}
    </div>}
    {selection && <button className="btn" disabled={busy || objects.data?.objects.find((item) => item.selection === selection && item.size > 5_000_000) !== undefined}
      onClick={() => void inspect()}>Preview selected file</button>}
    {preview && <>
      <p role="status" className="text-xs font-medium">{preview.row_count.toLocaleString()} terms found, {preview.entity_count} data entities, {preview.unclassified_count} unclassified, {preview.alias_count} aliases, {preview.rejected_count} rows rejected</p>
      {preview.warning && <p role="alert" className="text-xs text-amber-600">{preview.warning}</p>}
      <label className="block text-xs text-muted">Header row
        <select className="input ml-2" value={headerRow ?? preview.header_row}
          onChange={(event) => void inspect(undefined, Number(event.target.value))}>
          {Array.from({ length: 10 }, (_, index) => <option key={index} value={index + 1}>{index + 1}</option>)}
        </select>
      </label>
      <div className="grid gap-2 sm:grid-cols-3">{FIELDS.map((field) => <label key={field} className="text-xs">
        {field.replaceAll("_", " ")}{field === "term" || field === "definition" ? " *" : ""}
        <select className="input mt-1" value={mapping[field] ?? ""} onChange={(event) => {
          const value = event.target.value;
          setMapping((old) => { const next = { ...old }; if (value) next[field] = Number(value); else delete next[field]; return next; });
        }}>
          <option value="">Not mapped</option>
          {preview.headers.map((header, index) => <option key={index} value={index}>{index + 1}: {header || "(blank)"}</option>)}
        </select>
      </label>)}</div>
      {currentPreview !== JSON.stringify(mapping) && <button className="btn" disabled={busy}
        onClick={() => void inspect(mapping, headerRow)}>Refresh preview</button>}
      {preview.rejected_rows.length > 0 && <details className="text-xs"><summary>Unreadable rows ({preview.rejected_count})</summary>
        <ul>{preview.rejected_rows.map((row) => <li key={row.row}>Row {row.row}: {row.reason}</li>)}</ul>
      </details>}
      <button className="btn-primary" disabled={busy || currentPreview !== JSON.stringify(mapping) || !preview.row_count || mapping.term === undefined || mapping.definition === undefined}
        onClick={() => void stage()}>Stage {preview.row_count} proposals for review</button>
    </>}
    {staged && <p role="status" className="text-xs text-good">{staged.row_count} terms staged; {staged.created_count} new proposals. <Link to="/catalog" className="underline">Review proposals in Catalog</Link>.</p>}
    {error && <ErrorBox error={error} />}
  </section>;
}
